import { useEffect, useState } from 'react';
import { api } from '@/api/growth/dashboard/api';
import {
    GrowthDashboardReq,
    NutrientFractionationInfo,
} from '@/api/growth/dashboard/type';

interface Props {
    gfIdx: number;
}

const GrowthDashboardNutrientFractionationCard = ({ gfIdx }: Props) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<NutrientFractionationInfo | null>(null);

    useEffect(() => {
        fetchData();
    }, [gfIdx]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const req: GrowthDashboardReq = { gfIdx };
            const response =
                await api.getGrowingNutrientFractionationCardInfo(req);
            setData(response.data);
        } catch (error) {
            console.error(
                'Failed to fetch nutrient fractionation data:',
                error,
            );
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="card custom-card p-2 weather-card">
                <div
                    className="card-body d-flex justify-content-center align-items-center"
                    style={{ minHeight: '200px' }}
                >
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="card custom-card p-2 weather-card">
                <div className="card-body text-center text-muted">
                    데이터를 불러올 수 없습니다.
                </div>
            </div>
        );
    }

    return (
        <div className="card custom-card p-2 weather-card">
            <div className="card-header">
                <div className="w-100">
                    <h4 className="mb-1">토양양분/수분</h4>
                </div>
            </div>

            <div className="card-body weather-info">
                {/* 질소 섹션 */}
                <h6>질소</h6>
                <div className="row weather-grid">
                    <div className="col-12 col-sm-3 weather-item weather-item-header">
                        질소 시비량
                    </div>
                    <div className="col-12 col-sm-3 weather-item">
                        {data.nFertilizer.toFixed(2)} kg/ha
                    </div>
                    <div className="col-12 col-sm-3 weather-item weather-item-header">
                        가용 질소
                    </div>
                    <div className="col-12 col-sm-3 weather-item">
                        {data.nAbility.toFixed(2)} kg/ha
                    </div>
                </div>

                <div className="row weather-grid">
                    <div className="col-12 col-sm-3 weather-item weather-item-header">
                        질소 고정
                    </div>
                    <div className="col-12 col-sm-3 weather-item">
                        {data.nFixation.toFixed(2)} kg/ha
                    </div>
                    <div className="col-12 col-sm-3 weather-item weather-item-header">
                        질소 증발산량
                    </div>
                    <div className="col-12 col-sm-3 weather-item">
                        {data.nEt.toFixed(2)} kg/ha
                    </div>
                </div>

                <div className="row weather-grid">
                    <div className="col-12 col-sm-3 weather-item weather-item-header">
                        질소 용탈량
                    </div>
                    <div className="col-12 col-sm-3 weather-item">
                        {data.nDrainage.toFixed(2)} kg/ha
                    </div>
                    <div className="col-12 col-sm-3 weather-item weather-item-header"></div>
                    <div className="col-12 col-sm-3 weather-item"></div>
                </div>

                {/* 인산 섹션 */}
                <h6 className="mt-2">인산</h6>
                <div className="row weather-grid">
                    <div className="col-12 col-sm-3 weather-item weather-item-header">
                        가용 인산
                    </div>
                    <div className="col-12 col-sm-3 weather-item">
                        {data.pAbility.toFixed(2)} kg/ha
                    </div>
                    <div className="col-12 col-sm-3 weather-item weather-item-header">
                        인산 시비량
                    </div>
                    <div className="col-12 col-sm-3 weather-item">
                        {data.pFertilizer.toFixed(2)} kg/ha
                    </div>
                </div>

                <div className="row weather-grid">
                    <div className="col-12 col-sm-3 weather-item weather-item-header">
                        인산 용탈량
                    </div>
                    <div className="col-12 col-sm-3 weather-item">
                        {data.pDrainage.toFixed(2)} kg/ha
                    </div>
                    <div className="col-12 col-sm-3 weather-item weather-item-header"></div>
                    <div className="col-12 col-sm-3 weather-item"></div>
                </div>

                {/* 수분 섹션 */}
                <h6 className="mt-2">수분</h6>
                <div className="row weather-grid">
                    <div className="col-12 col-sm-3 weather-item weather-item-header">
                        토양 수분
                    </div>
                    <div className="col-12 col-sm-3 weather-item">
                        {data.soilWaterTotal.toFixed(2)} mm
                    </div>
                    <div className="col-12 col-sm-3 weather-item weather-item-header">
                        가용 수분
                    </div>
                    <div className="col-12 col-sm-3 weather-item">
                        {data.rootZoneWater.toFixed(2)} mm
                    </div>
                </div>

                <div className="row weather-grid">
                    <div className="col-12 col-sm-3 weather-item weather-item-header">
                        관수량
                    </div>
                    <div className="col-12 col-sm-3 weather-item">
                        {data.irrigationDaily.toFixed(2)} mm
                    </div>
                    <div className="col-12 col-sm-3 weather-item weather-item-header">
                        누적 강우량
                    </div>
                    <div className="col-12 col-sm-3 weather-item">
                        {data.precipitation.toFixed(2)} mm
                    </div>
                </div>

                <div className="row weather-grid">
                    <div className="col-12 col-sm-3 weather-item weather-item-header">
                        용탈량
                    </div>
                    <div className="col-12 col-sm-3 weather-item">
                        {data.waterDrainage.toFixed(2)} mm
                    </div>
                    <div className="col-12 col-sm-3 weather-item weather-item-header">
                        유실량
                    </div>
                    <div className="col-12 col-sm-3 weather-item">
                        {data.waterRunoff.toFixed(2)} mm
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GrowthDashboardNutrientFractionationCard;
